import { TabMenu } from '$shared/components/tab-menu';
import { useTranslation } from '$shared/utils';
import { useProduct } from '$shared/utils/product';
import React, { FC, ReactNode, useMemo } from 'react';
import { PdpDownloads } from './pdp-downloads';
import { ProductListTab } from './product-list-tab/product-list-tab';
import {
    PdpRelationshipsFilter
} from "$templates/pages/components/P50ProductDetailPage/components/pdp-tabs/pdp-relationships/pdp-relationships-filter";
import {FILTER_HOLDER_KEY, FILTER_INSERT_KEY} from "$utils/filter/service/constants";
import {
    parseProductContent
} from "$templates/pages/components/P50ProductDetailPage/components/pdp-tabs/pdp-relationships/product-data-parser";
import { PdpCuttingData } from './pdp-cutting-data/pdp-cutting-data';

export const PdpTabs: FC = () => {
    const { translate } = useTranslation();
    const {
        associatedProductOriginalItemNumbers: accessories = [],
        dataSheetPaths,
        sparePartsProductOriginalItemNumbers: spareParts = [],
        similarProductOriginalItemNumbers: similarProducts = [],
        productData: productData = [],
        cuttingData,
        description1,
        description2,
        description3,
    } = useProduct();

    const tabs = useMemo(() => {
        const tabs = new Array<{ tabName: string; content: ReactNode }>();

        if (accessories.length) {
            tabs.push({
                tabName: translate('product-detail-page.tabs.accessories'),
                content: <ProductListTab sanitizedItemNumbers={accessories} />,
            });
        }

        if (spareParts.length) {
            tabs.push({
                tabName: translate('product-detail-page.tabs.spare-parts'),
                content: <ProductListTab sanitizedItemNumbers={spareParts} />,
            });
        }

        if (similarProducts.length) {
            tabs.push({
                tabName: translate('product-detail-page.tabs.similar-products'),
                content: <ProductListTab sanitizedItemNumbers={similarProducts} />,
            });
        }

        if (dataSheetPaths?.length) {
            tabs.push({
                tabName: translate('product-detail-page.tabs.downloads'),
                content: <PdpDownloads />,
            });
        }

        const parsedProductData = productData.map(product => ({
            ...product,
            parsedContent: parseProductContent(product.content),
        }));

        if (
            parsedProductData.some(
                product =>
                    product.parsedContent &&
                    typeof product.parsedContent === 'object' &&
                    FILTER_HOLDER_KEY in product.parsedContent
            )
        ) {
            tabs.push({
                tabName: "Holder Relationships",
                content: <PdpRelationshipsFilter relationshipKey={FILTER_HOLDER_KEY} />,
            });
        }

        if (
            parsedProductData.some(
                product =>
                    product.parsedContent &&
                    typeof product.parsedContent === 'object' &&
                    FILTER_INSERT_KEY in product.parsedContent
            )
        ) {
            tabs.push({
                tabName: "Insert Relationships",
                content: <PdpRelationshipsFilter relationshipKey={FILTER_INSERT_KEY} />,
            });
        }

        const productName = [description1, description2, description3]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        const isHolderProduct = productName.includes('holder');
        const hasCuttingData = Boolean(cuttingData?.data);

        if (hasCuttingData && !isHolderProduct) {
            tabs.push({
                tabName: 'Cutting conditions',
                content: <PdpCuttingData />,
            });
        }

        return tabs;
    }, [
        accessories,
        dataSheetPaths,
        similarProducts,
        spareParts,
        productData,
        cuttingData,
        description1,
        description2,
        description3,
        translate,
    ]);

    return tabs.length ? <TabMenu tabs={tabs} /> : null;
};
